import React, { useState, useRef, useCallback } from 'react'
import { useSelector } from 'react-redux'
import { useNavigate } from 'react-router-dom'
import {
  Smartphone, Shirt, HeartPulse, Sparkles, UtensilsCrossed, Coffee,
  Dumbbell, Tent, Sofa, TreePine, Car, Building2, Briefcase, Wrench,
  GraduationCap, PawPrint, Palette, Tag, ChevronRight, ChevronDown, TrendingUp, Clock
} from 'lucide-react'

/* ── Map category name → Lucide icon ────────────────────────────── */
const CATEGORY_ICON_MAP = [
  { keys: ['electron'],                             icon: Smartphone, color: '#1976D2' },
  { keys: ['fashion', 'cloth', 'wear', 'apparel'],  icon: Shirt,      color: '#43A047' },
  { keys: ['health'],                               icon: HeartPulse,  color: '#D81B60' },
  { keys: ['beauty', 'cosmetic'],                   icon: Sparkles,    color: '#8E24AA' },
  { keys: ['food'],                                 icon: UtensilsCrossed, color: '#E64A19' },
  { keys: ['beverage', 'drink'],                    icon: Coffee,      color: '#EF6C00' },
  { keys: ['sport'],                                icon: Dumbbell,    color: '#00796B' },
  { keys: ['outdoor'],                              icon: Tent,        color: '#388E3C' },
  { keys: ['home', 'furniture'],                    icon: Sofa,        color: '#6D4C41' },
  { keys: ['garden'],                               icon: TreePine,    color: '#689F38' },
  { keys: ['auto', 'vehicle', 'car'],               icon: Car,         color: '#455A64' },
  { keys: ['real', 'estate', 'property', 'land'],   icon: Building2,   color: '#283593' },
  { keys: ['job', 'career', 'hire'],                icon: Briefcase,   color: '#00838F' },
  { keys: ['service'],                              icon: Wrench,      color: '#0288D1' },
  { keys: ['education', 'school', 'learn', 'book'], icon: GraduationCap, color: '#1976D2' },
  { keys: ['animal', 'pet'],                        icon: PawPrint,    color: '#33691E' },
  { keys: ['art', 'collect'],                       icon: Palette,     color: '#512DA8' },
]

const getCategoryIcon = (name = '') => {
  const n = name.toLowerCase()
  const match = CATEGORY_ICON_MAP.find(c => c.keys.some(k => n.includes(k)))
  return match || { icon: Tag, color: '#607D8B' }
}

/* ── Popular searches (static suggestions) ──────────────────────── */
const POPULAR_SEARCHES = [
  'iPhone 15', 'Samsung Galaxy', 'Laptop', 'PlayStation 5',
  'Nike Sneakers', 'Generator', 'Toyota Camry', 'Human Hair',
]

const SearchDropdown = ({ visible, onClose, searchValue }) => {
  const { categorys } = useSelector(state => state.home)
  const navigate = useNavigate()
  const [expandedId, setExpandedId] = useState(null)

  // Wrap navigate so we close AFTER navigation is queued
  const go = useCallback((path) => {
    navigate(path)
    // Small delay so React router processes before unmount
    setTimeout(() => onClose(), 0)
  }, [navigate, onClose])

  if (!visible) return null

  // Filter categories by search value
  const filtered = searchValue?.trim()
    ? categorys.filter(cat => {
        const q = searchValue.toLowerCase()
        if (cat.name.toLowerCase().includes(q)) return true
        if (cat.subcategories?.some(sub => sub.toLowerCase().includes(q))) return true
        return false
      })
    : categorys

  const showPopular = !searchValue?.trim()
  const displayCategories = filtered.slice(0, 12)

  const handleCategoryClick = (cat) => {
    go(`/products?category=${cat.name}`)
  }

  const handleSubcategoryClick = (e, catName, sub) => {
    e.stopPropagation()
    go(`/products?category=${catName}&subcategory=${sub}`)
  }

  const handlePopularClick = (term) => {
    go(`/products/search?category=&value=${term}`)
  }

  const toggleExpand = (e, catId) => {
    e.stopPropagation()
    setExpandedId(prev => prev === catId ? null : catId)
  }

  return (
    <div
      className="absolute top-full left-0 right-0 mt-1 bg-white rounded-xl border border-gray-200 shadow-xl z-[100] overflow-hidden"
      style={{ maxHeight: '70vh' }}
      onMouseDown={(e) => e.preventDefault()}
    >
      {/* Popular searches — only when input is empty */}
      {showPopular && (
        <div className="px-4 pt-3.5 pb-2.5 border-b border-gray-100">
          <div className="flex items-center gap-1.5 text-[11px] font-semibold text-gray-400 uppercase tracking-wider mb-2.5">
            <TrendingUp size={12} />
            Popular Searches
          </div>
          <div className="flex flex-wrap gap-1.5">
            {POPULAR_SEARCHES.map((term) => (
              <button
                key={term}
                type="button"
                onClick={() => handlePopularClick(term)}
                className="px-3 py-1.5 text-xs font-medium text-gray-600 bg-gray-50 hover:bg-green-50 hover:text-green-700 active:bg-green-100 rounded-full border border-gray-100 transition-colors cursor-pointer"
              >
                {term}
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Searching hint — no matches */}
      {searchValue?.trim() && filtered.length === 0 && (
        <div className="px-4 py-6 text-center text-sm text-gray-400">
          <Clock size={20} className="mx-auto mb-2 text-gray-300" />
          No matching categories — press Enter to search all products
        </div>
      )}

      {/* Category list */}
      {displayCategories.length > 0 && (
        <div>
          <div className="px-4 pt-3 pb-1.5 text-[11px] font-semibold text-gray-400 uppercase tracking-wider flex items-center gap-1.5">
            <Tag size={11} />
            {searchValue?.trim() ? 'Matching Categories' : 'Browse Categories'}
          </div>
          <div className="max-h-[50vh] overflow-y-auto overscroll-contain pb-2">
            {displayCategories.map((cat) => {
              const { icon: Icon, color } = getCategoryIcon(cat.name)
              const hasSubs = cat.subcategories && cat.subcategories.length > 0
              const isExpanded = expandedId === cat._id

              return (
                <div key={cat._id}>
                  {/* Category row */}
                  <div
                    className={`flex items-center gap-3 px-4 py-2.5 transition-colors cursor-pointer active:bg-green-50 ${
                      isExpanded ? 'bg-green-50/60' : 'hover:bg-gray-50'
                    }`}
                    role="button"
                    tabIndex={0}
                    onClick={() => {
                      if (hasSubs) {
                        setExpandedId(prev => prev === cat._id ? null : cat._id)
                      } else {
                        handleCategoryClick(cat)
                      }
                    }}
                  >
                    {/* Icon badge */}
                    <div
                      className="w-8 h-8 rounded-lg flex items-center justify-center shrink-0"
                      style={{ backgroundColor: `${color}14` }}
                    >
                      <Icon size={17} style={{ color }} />
                    </div>
                    {/* Name + count */}
                    <div className="flex-1 min-w-0">
                      <span className="text-sm font-medium text-gray-800 block truncate">
                        {cat.name}
                      </span>
                      {hasSubs && (
                        <span className="text-[11px] text-gray-400">
                          {cat.subcategories.length} subcategories
                        </span>
                      )}
                    </div>
                    {/* Expand/chevron or "View all" */}
                    {hasSubs ? (
                      <button
                        type="button"
                        onClick={(e) => toggleExpand(e, cat._id)}
                        className="shrink-0 w-7 h-7 flex items-center justify-center rounded-full hover:bg-gray-100 active:bg-gray-200 transition-colors"
                      >
                        {isExpanded
                          ? <ChevronDown size={15} className="text-green-500" />
                          : <ChevronRight size={15} className="text-gray-400" />
                        }
                      </button>
                    ) : (
                      <ChevronRight size={14} className="shrink-0 text-gray-300" />
                    )}
                  </div>

                  {/* Subcategories — inline expand */}
                  {isExpanded && hasSubs && (
                    <div className="bg-gray-50/80 px-4 py-2.5 border-t border-b border-gray-100">
                      {/* "View all" link */}
                      <button
                        type="button"
                        onClick={() => handleCategoryClick(cat)}
                        className="text-xs font-semibold text-green-600 hover:text-green-700 active:text-green-800 mb-2 cursor-pointer"
                      >
                        View all in {cat.name} →
                      </button>
                      <div className="flex flex-wrap gap-1.5">
                        {cat.subcategories.map((sub) => (
                          <button
                            key={sub}
                            type="button"
                            onClick={(e) => handleSubcategoryClick(e, cat.name, sub)}
                            className="px-2.5 py-1.5 text-xs font-medium text-gray-600 bg-white hover:bg-green-50 hover:text-green-700 active:bg-green-100 rounded-lg border border-gray-200 transition-colors cursor-pointer"
                          >
                            {sub}
                          </button>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              )
            })}
          </div>
        </div>
      )}

      {/* Footer */}
      {displayCategories.length > 0 && categorys.length > 12 && !searchValue?.trim() && (
        <div className="px-4 py-2.5 bg-gray-50 border-t border-gray-100 text-center">
          <button
            type="button"
            onClick={() => go('/products?category=')}
            className="text-xs font-semibold text-green-600 hover:text-green-700 active:text-green-800 transition-colors cursor-pointer"
          >
            View all {categorys.length} categories →
          </button>
        </div>
      )}
    </div>
  )
}

export default SearchDropdown
